import { Check } from "lucide-react";
import { cn } from "@/design-system/lib/utils";

// LLM & Reports v3.2 §8.1 — progress strip surfaced at the top of the
// generate flow so users can see where they are. The existing
// /reports/generate page renders steps 1-6 as a vertical scroll;
// rather than restructure into a true stepper (high regression risk
// against the v3.1 form-fill machinery), we render this strip above
// the page so users get the seven-step model the spec describes
// without the scrolling experience changing.
//
// Stage 1's post-Drafted detail page handles steps 7-8 separately;
// it does not show this strip — the user lands there from the reports
// list, not by clicking through.

export type WizardStepKey =
  | "title"
  | "form"
  | "documents"
  | "review"
  | "generate";

interface StepDef {
  key: WizardStepKey;
  index: number;
  label: string;
}

const STEPS: StepDef[] = [
  { key: "title", index: 1, label: "Title" },
  { key: "form", index: 2, label: "Form" },
  { key: "documents", index: 3, label: "Documents" },
  { key: "review", index: 4, label: "Prompt review" },
  { key: "generate", index: 5, label: "Generate" },
];

export interface WizardProgressProps {
  /** Highest step the user has reached so far. Index 1-5 of STEPS. */
  currentStep: WizardStepKey;
  /** Steps that have been completed (used to show the green checkmark). */
  completedSteps?: WizardStepKey[];
  className?: string;
}

export function WizardProgress({
  currentStep,
  completedSteps = [],
  className,
}: WizardProgressProps) {
  const currentIndex = STEPS.findIndex((s) => s.key === currentStep) + 1;

  return (
    <ol
      role="list"
      aria-label="Generate report progress"
      className={cn(
        "flex items-center justify-between gap-space-2",
        "rounded-md border border-border-light bg-white-pure px-space-4 py-space-3",
        className,
      )}
    >
      {STEPS.map((step, i) => {
        const isCompleted = completedSteps.includes(step.key);
        const isCurrent = step.key === currentStep;
        const isReached = step.index <= currentIndex;
        return (
          <li key={step.key} className="flex flex-1 items-center gap-space-2 min-w-0">
            <span
              aria-current={isCurrent ? "step" : undefined}
              className={cn(
                "inline-flex h-7 w-7 items-center justify-center rounded-full",
                "font-display text-caption font-bold uppercase shrink-0",
                isCompleted
                  ? "bg-status-success text-white-pure"
                  : isCurrent
                    ? "bg-gold text-onyx ring-2 ring-gold-shadow"
                    : isReached
                      ? "bg-gold-highlight/40 text-onyx"
                      : "bg-white-warm text-gray-mid",
              )}
            >
              {isCompleted ? <Check size={14} aria-hidden /> : step.index}
            </span>
            <span
              className={cn(
                "font-body text-caption truncate",
                isCurrent ? "text-onyx font-semibold" : "text-gray-mid",
              )}
            >
              {step.label}
            </span>
            {i < STEPS.length - 1 && (
              <span
                aria-hidden
                className={cn(
                  "h-px flex-1 ml-space-1",
                  isReached ? "bg-gold-shadow/60" : "bg-border-light",
                )}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}
